import {DateTime} from 'luxon';
import {SavedExportSchedulePeriod} from '../../saved-exports/saved-export.model';

const previewedDeliveries = 4;
const leapCycleMonths = 48;

export type MonthlyDeliveryPlan = {
    startDate: DateTime;
    interval: number;
    dayOfMonth: number;
    period: SavedExportSchedulePeriod;
    daysBackInTime: number | null;
};

export type PlannedDelivery = {
    sentOn: DateTime;
    holdsFrom: DateTime | null;
    holdsTo: DateTime;
};

function utcDay(value: DateTime): DateTime {
    return DateTime.utc(value.year, value.month, value.day);
}

export function endedMonthsWindowDays(interval: number, now: DateTime): number | null {
    if (!Number.isInteger(interval) || interval < 1) {
        return null;
    }
    const firstDelivery = utcDay(now.toUTC()).startOf('month').plus({months: 1});
    return Math.max(
        ...Array.from({length: leapCycleMonths}, (_, monthsLater) => {
            const delivery = firstDelivery.plus({months: monthsLater});
            return delivery.diff(delivery.minus({months: interval}), 'days').days;
        }),
    );
}

function firstDeliveryMonth(startDate: DateTime, dayOfMonth: number): DateTime {
    const month = utcDay(startDate).startOf('month');
    return startDate.day > dayOfMonth ? month.plus({months: 1}) : month;
}

export function nextMonthlyDeliveries(plan: MonthlyDeliveryPlan, now: DateTime): PlannedDelivery[] {
    const {startDate, interval, period, daysBackInTime} = plan;
    const dayOfMonth = period === 'ENDED_MONTHS' ? 1 : plan.dayOfMonth;
    const earliest = utcDay(now.toUTC());
    const firstMonth = firstDeliveryMonth(startDate, dayOfMonth);
    const monthsUntilEarliest = Math.max(
        0,
        Math.floor(earliest.startOf('month').diff(firstMonth, 'months').months),
    );
    const firstStep = Math.ceil(monthsUntilEarliest / interval);
    return Array.from({length: previewedDeliveries + 1}, (_, index) =>
        firstMonth.plus({months: (firstStep + index) * interval}).set({day: dayOfMonth}),
    )
        .filter(sentOn => sentOn >= earliest)
        .slice(0, previewedDeliveries)
        .map(sentOn => {
            if (daysBackInTime === null) {
                return {
                    sentOn,
                    holdsFrom: null,
                    holdsTo: period === 'ENDED_MONTHS' ? sentOn.minus({days: 1}) : sentOn,
                };
            }
            const holdsTo = sentOn.minus({days: 1});
            const holdsFrom =
                period === 'ENDED_MONTHS'
                    ? sentOn.minus({months: interval})
                    : holdsTo.minus({days: daysBackInTime - 1});
            return {sentOn, holdsFrom, holdsTo};
        });
}
